<template>
  <div class="app-announcements-host">
    <!-- One announcement at a time (desktop = full strip, mobile = compact) -->
    <div v-if="currentAnnouncement" :class="['announcement-stack', { 'announcement-stack--mobile': isMobile }]">
      <div
        :class="[
          'announcement-strip',
          `type-${currentAnnouncement.type}`,
          { 'announcement-strip--mobile': isMobile }
        ]"
      >
        <!-- Mobile compact layout -->
        <template v-if="isMobile">
          <div class="announcement-strip__mobile-top">
            <div class="announcement-strip__mobile-text" @click="handleClick(currentAnnouncement)">
              <div v-if="currentAnnouncement.title" class="announcement-strip__title">
                {{ currentAnnouncement.title }}
              </div>
              <div
                v-if="currentAnnouncement.message"
                :class="[
                  'announcement-strip__message-inline',
                  { 'is-scrolling': shouldScroll }
                ]"
                :style="shouldScroll ? { '--marquee-duration': scrollDuration + 's' } : {}"
              >
                <span>{{ currentAnnouncement.message }}</span>
              </div>
            </div>
            <span
              v-if="currentAnnouncement.dismissible"
              class="announcement-strip__mobile-dismiss"
              role="button"
              tabindex="0"
              @click.stop="$emit('dismiss', currentAnnouncement)"
            >
              &times;
            </span>
          </div>
        </template>

        <!-- Desktop full layout -->
        <template v-else>
          <div class="announcement-strip__body" @click="handleClick(currentAnnouncement)">
            <div v-if="currentAnnouncement.title" class="announcement-strip__title">
              {{ currentAnnouncement.title }}
            </div>
            <div
              :class="[
                'announcement-strip__message',
                { 'is-scrolling': shouldScroll }
              ]"
              :style="shouldScroll ? { '--marquee-duration': scrollDuration + 's' } : {}"
            >
              <span>{{ currentAnnouncement.message }}</span>
            </div>
          </div>
          <div class="announcement-strip__actions">
            <button
              v-if="currentAnnouncement.link"
              type="button"
              class="announcement-btn announcement-btn--primary"
              @click.stop="handleClick(currentAnnouncement)"
            >
              {{ currentAnnouncement.ctaLabel || 'Learn More' }}
            </button>
            <button
              v-if="currentAnnouncement.dismissible"
              type="button"
              class="announcement-btn announcement-btn--ghost"
              @click.stop="$emit('dismiss', currentAnnouncement)"
            >
              Close
            </button>
          </div>
        </template>

        <!-- Dots for both desktop and mobile -->
        <div v-if="topAnnouncements.length > 1" class="announcement-strip__dots">
          <span
            v-for="(ann, idx) in topAnnouncements"
            :key="ann.id"
            :class="['announcement-dot', { 'announcement-dot--active': idx === currentIndex }]"
            @click.stop="currentIndex = idx"
          />
        </div>
      </div>
    </div>

    <div
      v-if="modalAnnouncement"
      class="announcement-modal-backdrop"
      @click.self="$emit('dismiss', modalAnnouncement)"
    >
      <div class="announcement-modal">
        <button
          v-if="modalAnnouncement.dismissible"
          type="button"
          class="announcement-modal__close"
          aria-label="Close"
          @click="$emit('dismiss', modalAnnouncement)"
        >
          &times;
        </button>

        <div class="announcement-modal__badge">
          {{ modalAnnouncement.type || 'info' }}
        </div>
        <h2 class="announcement-modal__title">
          {{ modalAnnouncement.title || 'Announcement' }}
        </h2>
        <p class="announcement-modal__message">
          {{ modalAnnouncement.message }}
        </p>

        <img
          v-if="modalAnnouncement.imageUrl"
          :src="modalAnnouncement.imageUrl"
          :alt="modalAnnouncement.title || 'Announcement image'"
          class="announcement-modal__image"
        />

        <div class="announcement-modal__actions">
          <button
            v-if="modalAnnouncement.link"
            type="button"
            class="announcement-btn announcement-btn--primary"
            @click="handleClick(modalAnnouncement)"
          >
            {{ modalAnnouncement.ctaLabel || 'Learn More' }}
          </button>
          <button
            v-if="modalAnnouncement.requiresAcknowledgement"
            type="button"
            class="announcement-btn announcement-btn--primary"
            @click="$emit('acknowledge', modalAnnouncement)"
          >
            Acknowledge
          </button>
          <button
            v-else
            type="button"
            class="announcement-btn announcement-btn--secondary"
            @click="$emit('dismiss', modalAnnouncement)"
          >
            {{ modalAnnouncement.dismissible ? 'Close' : 'OK' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AppAnnouncementsHost",
  props: {
    announcements: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "all",
      validator: (value) => ["all", "top", "modal"].includes(value),
    },
  },
  data() {
    return {
      isMobile: false,
      currentIndex: 0,
      rotationTimer: null,
    };
  },
  computed: {
    modalAnnouncement() {
      if (this.mode === "top") return null;
      return this.announcements.find((item) => item.displayType === "modal") || null;
    },
    topAnnouncements() {
      if (this.mode === "modal") return [];
      return this.announcements
        .filter((item) => item.displayType !== "modal")
        .slice()
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
    },
    currentAnnouncement() {
      if (!this.topAnnouncements.length) return null;
      const idx = Math.min(this.currentIndex, this.topAnnouncements.length - 1);
      return this.topAnnouncements[Math.max(idx, 0)];
    },
    shouldScroll() {
      if (!this.currentAnnouncement) return false;
      return this.currentAnnouncement.autoScroll || this.currentAnnouncement.displayType === 'ticker';
    },
    scrollDuration() {
      if (!this.currentAnnouncement) return 18;
      const speed = this.currentAnnouncement.scrollSpeed;
      if (typeof speed === 'number' && speed > 0) return speed;
      const map = { slow: 28, normal: 18, fast: 10 };
      return map[speed] || 18;
    },
  },
  watch: {
    topAnnouncements(newVal) {
      if (this.currentIndex >= newVal.length) {
        this.currentIndex = 0;
      }
      this.startRotation();
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    this.startRotation();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
    this.stopRotation();
  },
  methods: {
    handleClick(announcement) {
      this.$emit("click", announcement);
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    startRotation() {
      this.stopRotation();
      if (this.topAnnouncements.length > 1) {
        this.rotationTimer = setInterval(() => {
          this.currentIndex = (this.currentIndex + 1) % this.topAnnouncements.length;
        }, 5000);
      }
    },
    stopRotation() {
      if (this.rotationTimer) {
        clearInterval(this.rotationTimer);
        this.rotationTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.app-announcements-host {
  position: relative;
  z-index: 1;
}

.announcement-stack {
  position: relative;
  z-index: 1;
}

.announcement-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.announcement-strip.type-info {
  background: linear-gradient(90deg, #1f5eff, #2877ff);
}

.announcement-strip.type-promo,
.announcement-strip.type-feature {
  background: linear-gradient(90deg, #0d9f6e, #16b77a);
}

.announcement-strip.type-warning {
  background: linear-gradient(90deg, #c55c00, #df7a16);
}

.announcement-strip__body {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.announcement-strip__title {
  font-weight: 700;
  margin-bottom: 2px;
}

.announcement-strip__message {
  overflow: hidden;
  white-space: nowrap;
  opacity: 0.96;
}

.announcement-strip__message.is-scrolling span {
  display: inline-block;
  padding-left: 100%;
  animation: announcement-marquee var(--marquee-duration, 18s) linear infinite;
}

.announcement-strip__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.announcement-btn {
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 700;
  padding: 9px 14px;
}

.announcement-btn--primary {
  background: #fff;
  color: #16327a;
}

.announcement-btn--secondary {
  background: #16327a;
  color: #fff;
}

.announcement-btn--ghost {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.announcement-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(7, 18, 36, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1200;
}

.announcement-modal {
  position: relative;
  width: min(560px, 100%);
  background: #fff;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.28);
}

.announcement-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: #eef2ff;
  color: #16327a;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.announcement-modal__badge {
  display: inline-block;
  margin-bottom: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #e7f0ff;
  color: #18439c;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.announcement-modal__title {
  margin: 0 0 10px;
  color: #102040;
  font-size: 28px;
  font-weight: 800;
}

.announcement-modal__message {
  margin: 0 0 18px;
  color: #44516a;
  line-height: 1.6;
}

.announcement-modal__image {
  width: 100%;
  max-height: 30vh;
  object-fit: contain;
  border-radius: 16px;
  margin-bottom: 18px;
}

.announcement-modal__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

@keyframes announcement-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* Mobile single-announcement view */
.announcement-strip--mobile {
  flex-direction: column;
  padding: 8px 12px;
  gap: 4px;
}

.announcement-strip__mobile-top {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  min-height: 28px;
}

.announcement-strip__mobile-text {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  line-height: 1.4;
}

.announcement-strip--mobile .announcement-strip__title {
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.announcement-strip__message-inline {
  font-size: 12px;
  opacity: 0.9;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
}

.announcement-strip__message-inline.is-scrolling {
  text-overflow: clip;
}

.announcement-strip__message-inline.is-scrolling span {
  display: inline-block;
  padding-left: 100%;
  animation: announcement-marquee var(--marquee-duration, 14s) linear infinite;
}

.announcement-strip__mobile-dismiss {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none !important;
  border-radius: 50%;
  background: none !important;
  background-color: transparent !important;
  -webkit-appearance: none;
  appearance: none;
  outline: none;
  box-shadow: none !important;
  color: rgba(255, 255, 255, 0.75);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

/* Dots navigation */
.announcement-strip__dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 2px 0;
}

.announcement-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.announcement-dot--active {
  background: #fff;
  transform: scale(1.3);
}

/* Mobile dots smaller */
.announcement-strip--mobile .announcement-strip__dots {
  padding: 1px 0 2px;
  gap: 4px;
}

.announcement-strip--mobile .announcement-dot {
  width: 4px;
  height: 4px;
}

@media (max-width: 768px) {
  .announcement-modal {
    width: min(560px, 95%);
    padding: 20px;
    border-radius: 20px;
    max-height: 85vh;
    overflow-y: auto;
  }

  .announcement-modal__title {
    font-size: 20px;
  }

  .announcement-modal__message {
    font-size: 14px;
    max-height: 40vh;
    overflow-y: auto;
  }

  .announcement-modal__badge {
    font-size: 10px;
    padding: 4px 8px;
  }

  .announcement-modal__close {
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    font-size: 20px;
  }

  .announcement-modal__actions {
    flex-direction: column;
    gap: 8px;
  }

  .announcement-modal__actions .announcement-btn {
    width: 100%;
    text-align: center;
  }
}
</style>